import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import * as microsoftTeams from "@microsoft/teams-js";
import { Button, Dropdown, Flex, Input, Tooltip } from "@fluentui/react-northstar";
import { ComposeIcon, RetryIcon, SearchIcon } from "@fluentui/react-icons-northstar";
import filter from "lodash/filter";
import find from "lodash/find";
import Alert from "../components/common/alert";
import TaskList from "../components/taskList";
import ITask from "../interfaces/ITask";

import { useAuth } from "../contexts/authContext";
import { useApp } from "../contexts/appContext";
import { useInvalidateData, useTasks } from "../queries";
import { getErrorMessage, isAuthIssue } from "../queries/queryClient";
import { completeTask } from "../services/dataService";
import styles from "../common.module.scss";
import { getSelectedTaskView, setSelectedTaskView } from "../services/settingsService";
import { parseJwt } from "../common/textOperations";

const viewNameToViewId = (value: string): number => {
    switch (value) {
        case "Moje": return 0;
        case "Mnou přiřazené": return 1;
        case "Všechny": return 2;
        default: return 3;
    }
};

const views = ["Moje", "Mnou přiřazené", "Všechny", "Uzavřené"];

const Tasks: React.FunctionComponent<{}> = () => {
    document.title = "Tasks";
    const history = useHistory();
    const { state: authState } = useAuth();
    const { accessToken: token } = authState;
    const { state: appState, dispatch } = useApp();
    const { backgroundColor } = appState;

    const [query, setQuery] = useState(appState.tasksQuery);
    const [canSeeAll, setCanSeeAll] = useState(false);
    const [selectedView, setSelectedView] = useState(getSelectedTaskView());

    const { data: tasksData, error, refetch } = useTasks(viewNameToViewId(selectedView));
    const items: ITask[] = useMemo(() => tasksData ?? [], [tasksData]);
    const { invalidateTasks } = useInvalidateData();

    useEffect(() => {
        if (!token) { return; }
        const jwtData = parseJwt(token);
        const group = find(jwtData.groups, (g: string) => g === import.meta.env.VITE_ALL_TASKS_GROUP_ID)
        setCanSeeAll(group && group.length > 0);
    }, [token]);

    useEffect(() => {
        dispatch({ type: "SET_TASKS_QUERY", query });
    }, [query]);

    const filteredItems = useMemo(() => {
        if (!query) { return items; }

        const lower = query.toLowerCase();
        const words = lower.split(" ");

        return filter(items, (x: ITask) => {
            const subject = x.subject.toLowerCase();
            const regarding = x.regarding?.title?.toLowerCase();
            const owner = x.owner?.displayName?.toLowerCase();
            const author = x.author?.displayName?.toLowerCase();
            const searchInValue = `${subject} ${regarding} ${owner} ${author}`;

            // tslint:disable-next-line: no-shadowed-variable
            const match: boolean[] = words.map((_: any) => false);
            for (let index = 0; index < words.length; index++) {
                match[index] = searchInValue.indexOf(words[index]) > -1;
            }

            return match.filter((m: boolean) => m).length === match.length;
        });
    }, [query, items]);

    const viewChanged = async (_: any, data: any) => {
        const value = data.value;
        setSelectedView(value);
        setSelectedTaskView(value);
    };

    const toggleLoading = (loading: boolean) => {
        dispatch({ type: "SET_LOADER", show: loading });
    };

    const modalResultClose = async (_: any) => {
        invalidateTasks();
    };

    const showModalClose = (id: string, projectId: string) => {
        const dialogInfo: microsoftTeams.UrlDialogInfo = {
            title: "Potvrzení",
            size: {
                height: 310,
                width: 400
            },
            url: import.meta.env.VITE_BASE_URL + `/modal/project/confirmtask?id=${id}&projectId=${projectId}`
        };

        microsoftTeams.dialog.url.open(dialogInfo, modalResultClose);
    };

    const completeTaskClicked = async (id: string, projectId?: string) => {
        const item = find(items, (x: ITask) => x.id === id);

        if (item?.regarding?.typeCode === "incident") {
            showModalClose(id, projectId ?? "");
        } else {
            if (!token) { return; }
            try {
                toggleLoading(true);
                await completeTask(token, id);
                invalidateTasks();
            } catch (error) {

            } finally {
                toggleLoading(false);
            }
        }
    };

    return (
        <div>
            <div className={styles.centerContainer} style={{ minHeight: 100 }}>
                <Flex className={styles.stickySearch} style={{ backgroundColor }}>
                    <Dropdown
                        defaultValue={selectedView}
                        items={canSeeAll ? views : views.slice(0, 2)}
                        onChange={viewChanged}
                        style={{ padding: "1em 0 1em 1em", width: "180px" }}
                        fluid
                    />
                    <Input
                        value={query} placeholder="Vyhledávání" onChange={(_: any, data: any) => setQuery(data.value)}
                        icon={<SearchIcon />} style={{ padding: "1em" }}
                        clearable fluid
                    />
                </Flex>
                {error && <Alert type="error" authIssue={isAuthIssue(error)}>{getErrorMessage(error)}</Alert>}
                <TaskList items={filteredItems} completeTask={completeTaskClicked} showOwner={viewNameToViewId(selectedView) !== 0} />
            </div>

            <div className={styles.footer} style={{ background: backgroundColor }}>
                <Flex gap="gap.small" className={styles.centerContainer}>
                    <Tooltip
                        trigger={<Button text iconOnly icon={<RetryIcon />} onClick={() => refetch()} />}
                        content="Aktualizovat seznam úkolů"
                    />
                    <Flex.Item push>
                        <Button content="Nový" primary icon={<ComposeIcon />} onClick={() => history.push("/task/detail")} />
                    </Flex.Item>
                </Flex>
            </div>
        </div>
    );
};

export default Tasks;
